import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import {
  ManagerInput,
  Manager as ManagerResponse,
  MutationCreateManagerArgs,
  MutationDeleteManagerArgs,
  MutationUpdateManagerArgs,
} from '../../../generated/graphql-models';
import { Permission } from '../../shared/models/api/permissions.enum';
import { GraphQLService } from '../../shared/services/graphql.service';
import { PermissionService } from '../../shared/services/permission.service';
import {
  CREATE_MANAGER,
  DELETE_MANAGER,
  GET_ALL_MANAGERS,
  GET_ALL_MANAGERS_RAW,
  UPDATE_MANAGER,
} from '../constants/manager.queries';
import { Manager } from '../models/manager.model';

// Must mirror the server-side gate on `getAllManagersUnfiltered`, or the list load 403s.
const UNFILTERED_MANAGERS_PERMISSIONS = [Permission.WRITE_CATALOG, Permission.READ_MANAGE_DEAL_QUEUE];

@Injectable({ providedIn: 'root' })
export class ManagerService {
  private readonly graphQlService = inject(GraphQLService);
  private readonly permissionService = inject(PermissionService);

  /**
   * All managers the user may see: the full catalog for catalog / deal-queue users, otherwise
   * the ones inside their workspace.
   */
  public getAll(): Observable<Manager[]> {
    return this.permissionService.hasAnyPermission(UNFILTERED_MANAGERS_PERMISSIONS).pipe(
      take(1),
      switchMap((canReadUnfiltered) =>
        this.graphQlService.getManyQuery<ManagerResponse>(canReadUnfiltered ? GET_ALL_MANAGERS_RAW : GET_ALL_MANAGERS),
      ),
      map((res) => res.map((m) => this.managerMapper(m))),
    );
  }

  public updateById(id: string, input: ManagerInput): Observable<Manager> {
    return this.graphQlService
      .mutate<ManagerResponse, MutationUpdateManagerArgs>(UPDATE_MANAGER, {
        _id: id,
        input,
      })
      .pipe(map((res) => this.managerMapper(res)));
  }

  public deleteById(id: string): Observable<Manager> {
    return this.graphQlService
      .mutate<ManagerResponse, MutationDeleteManagerArgs>(DELETE_MANAGER, {
        _id: id,
      })
      .pipe(map((res) => this.managerMapper(res)));
  }

  public createManager(input: ManagerInput): Observable<Manager> {
    return this.graphQlService
      .mutate<ManagerResponse, MutationCreateManagerArgs>(CREATE_MANAGER, {
        input,
      })
      .pipe(map((res) => this.managerMapper(res)));
  }

  public managerMapper(m: ManagerResponse): Manager {
    return {
      id: m?._id,
      managerId: m?.managerShortName,
      managerDisplayName: m?.displayName,
      assetShortName: m?.assetShortName,
    } as Manager;
  }
}
